import { type Product, type Link, type Vendor } from 'types/adtech';
import { progressiveGridSizing } from 'utils/gridUtils';
import { TRADING_CELL } from '@johnlewispartnership/wtr-content-component-library/dist/component-library/constants/cmsResourceTypes';
import { generateBannerLink, type RequiredBannerLink } from './generate-banner-link';

interface DeviceBreakpoints {
  width: string;
  name: string;
  auto: boolean;
  layout: string;
  visible: boolean;
}

export interface CitrusProduct extends Product {
  id: string;
  name: string;
}

interface Banner {
  id: string;
  name: string;
  type: string;
  gridPosition: string;
  adTechPosition: string;
  image: { src: string; altText: string; mode: string };
  textArea: {
    heading: string;
    color: string;
    subheading: string;
    offerHeading: string;
    link: Link;
  };
  products: CitrusProduct[];
  marketingBadge?: 'none' | 'New' | 'Try';
  cqResponsive: {
    deviceBreakpoints: DeviceBreakpoints[];
  };
  tracking?: {
    vendor: Vendor;
  };
}

interface ComponentProps {
  banner: Banner;
}

export interface TradingCellPropsFactoryProps {
  component: ComponentProps;
  position?: string;
  index?: number;
  searchType: string;
}

type BannerType = 'SponsoredBanner';
type AssetType = 'traffic_driver' | string;
type Offer = string;
type SKU = string;
type Campaign = string;

type SponsoredTag =
  `${Vendor}-${BannerType}-${AssetType}-${Offer}-${SKU}-${Banner['marketingBadge']}-${Campaign}`;

type AnalyticsTags = {
  'data-analytics-id': string;
  'data-analytics-name': string;
  'data-analytics-position': string;
  'data-analytics-sponsored': SponsoredTag;
};

export const transformSearchResultToCitrusTradingCell = ({
  component,
  position = '',
  index,
  searchType,
}: TradingCellPropsFactoryProps) => {
  const {
    banner: {
      textArea,
      id,
      name,
      image: { src, altText },
      marketingBadge,
      products,
      cqResponsive,
      tracking,
      type,
    },
  } = component;

  const productList = products || [];

  const breakpointClasses = progressiveGridSizing([], cqResponsive, 12, true);

  const link = generateBannerLink(
    textArea?.link?.url,
    productList,
    textArea?.link?.text,
    textArea?.link?.isExternalLink,
  );

  const analyticsTags: AnalyticsTags = {
    'data-analytics-id': id,
    'data-analytics-name': name,
    'data-analytics-position': position,
    'data-analytics-sponsored': `${tracking?.vendor || 'citrus'}-SponsoredBanner-${
      searchType || 'traffic_driver'
    }-${type || 'none'}-${textArea?.offerHeading.replace(/ /g, '') || 'none'}-${marketingBadge || 'none'}-${name}`,
  };

  return {
    ...component,
    externalImageSrc: true,
    products: productList.map(product => ({
      id: product.id,
    })),
    index,
    isCitrusAd: true,
    textArea: {
      ...textArea,
      link: {
        ...(link as RequiredBannerLink),
        ...(link.isExternalLink !== undefined && {
          newWindow: link.isExternalLink,
        }),
      },
    },
    position,
    componentId: id,
    cqResponsive,
    breakpointClasses,
    defaultNumOfCells: 'twoCells',
    resourceType: TRADING_CELL,
    icons: {
      messageIcon: {
        display: !!marketingBadge,
        data: {
          message: marketingBadge,
        },
      },
      offerRoundel: {
        display: !!textArea?.offerHeading,
        image: {
          altText: 'Offers',
          src: '/content/dam/waitrosegroceriescms/icons/offerroundels/offers.png',
        },
      },
    },
    image: {
      landscapeImage: { src, altText, assetMetadataHeight: '986', assetMetadataWidth: '1500' },
    },
    analyticsTags,
    analytics: {
      promoName: tracking?.vendor === 'kevel' ? tracking.vendor : ('citrusAd' as const),
      ...component.banner.tracking,
    },
  };
};
